import { useState, useEffect, useRef, useCallback } from 'react';
import { Tree } from 'react-arborist';
import API from '../api/axios';
import FolderModal from './FolderModal';

const ROW_HEIGHT = 32;
const INDENT = 16;

const TASK_MIME = 'application/x-focusly-task';

// Recursive node renderer — matches the app's Bootstrap styling.
function Node({ node, style, dragHandle, tree }) {
  const folder = node.data;
  const isSelected = folder.id === tree.props.selectedFolderId;
  const meta = tree.props.folderMeta;
  // Highlight when a task is being dragged over this folder (separate from
  // react-arborist's own folder-reorder drag, which uses react-dnd).
  const [taskDropActive, setTaskDropActive] = useState(false);

  const draggingTask = (e) => Array.from(e.dataTransfer.types).includes(TASK_MIME);

  return (
    <div
      ref={dragHandle}
      style={{
        ...style,
        ...(taskDropActive
          ? { boxShadow: 'inset 0 0 0 2px var(--bs-success)', borderRadius: 4 }
          : {}),
      }}
      className={`d-flex align-items-center rounded pe-1 h-100 ${
        isSelected ? 'bg-primary text-white' : 'hover-bg'
      } ${node.willReceiveDrop ? 'border border-primary' : ''}`}
      onDragOver={(e) => {
        if (!draggingTask(e)) return; // let arborist handle folder drags
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (!taskDropActive) setTaskDropActive(true);
      }}
      onDragLeave={() => { if (taskDropActive) setTaskDropActive(false); }}
      onDrop={(e) => {
        if (!draggingTask(e)) return;
        e.preventDefault();
        setTaskDropActive(false);
        const raw = e.dataTransfer.getData(TASK_MIME);
        if (!raw) return;
        let ids;
        try { ids = JSON.parse(raw); } catch { ids = [raw]; }
        meta.onMoveTaskToFolder(Array.isArray(ids) ? ids : [ids], folder.id);
      }}
    >
      <span
        className={`me-1 ${isSelected ? 'text-white' : 'text-muted'}`}
        style={{ width: 14, fontSize: '0.7rem', cursor: 'pointer', flexShrink: 0, textAlign: 'center' }}
        onClick={(e) => { e.stopPropagation(); node.toggle(); }}
      >
        {node.isLeaf ? '' : node.isOpen ? '▼' : '▶'}
      </span>

      {node.isEditing ? (
        <input
          className="form-control form-control-sm py-0 flex-grow-1"
          autoFocus
          defaultValue={folder.name}
          onClick={(e) => e.stopPropagation()}
          onBlur={() => node.reset()}
          onKeyDown={(e) => {
            if (e.key === 'Escape') node.reset();
            if (e.key === 'Enter') node.submit(e.currentTarget.value);
          }}
        />
      ) : (
        <span
          className="flex-grow-1 text-truncate"
          style={{ cursor: 'pointer' }}
          onClick={() => meta.onSelect(folder.id)}
          onDoubleClick={() => node.edit()}
        >
          {folder.name}
        </span>
      )}

      <div className="dropdown" onClick={(e) => e.stopPropagation()}>
        <button
          className={`btn btn-sm p-0 ${isSelected ? 'text-white' : 'text-muted'}`}
          data-bs-toggle="dropdown"
          style={{ lineHeight: 1, boxShadow: 'none', outline: 'none', border: 'none' }}
          onClick={(e) => {
            document.querySelectorAll('.dropdown-menu.show').forEach((m) => {
              if (m !== e.currentTarget.nextElementSibling) m.classList.remove('show');
            });
          }}
        >
          &#8942;
        </button>
        <ul className="dropdown-menu dropdown-menu-end">
          <li><button className="dropdown-item" onClick={() => meta.onCreate(folder.id)}>New subfolder</button></li>
          <li><button className="dropdown-item" onClick={() => node.edit()}>Rename</button></li>
          <li><hr className="dropdown-divider" /></li>
          <li><button className="dropdown-item text-danger" onClick={() => meta.onDelete(folder.id)}>Delete</button></li>
        </ul>
      </div>
    </div>
  );
}

export default function FolderTree({
  selectedFolderId, onSelectFolder, onFoldersLoaded, onFoldersChanged,
}) {
  const [folders, setFolders] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [parentIdForNew, setParentIdForNew] = useState(null);
  const [treeHeight, setTreeHeight] = useState(ROW_HEIGHT);
  const treeRef = useRef(null);

  const fetchFolders = useCallback(async () => {
    const { data } = await API.get('/folders');
    setFolders(data);
    return data;
  }, []);

  const refreshFolders = useCallback(async () => {
    const data = await fetchFolders();
    if (onFoldersChanged) onFoldersChanged();
    return data;
  }, [fetchFolders, onFoldersChanged]);

  useEffect(() => {
    fetchFolders().then((data) => { if (onFoldersLoaded) onFoldersLoaded(data); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Flat list -> nested tree (API is already sorted by sort_order)
  const buildTree = (items, parentId = null) =>
    items
      .filter((f) => f.parent_id === parentId)
      .map((f) => ({ ...f, children: buildTree(items, f.id) }));
  const treeData = buildTree(folders);

  // Size the tree to its visible rows so the parent sidebar scrolls (no nested scrollbar).
  // NB: react-arborist's `visibleNodes` array is stale inside the onToggle callback
  // (it's recomputed on the next render), but `isOpen()` reads the live store — so we
  // count visible rows ourselves from the data using isOpen().
  const syncHeight = useCallback(() => {
    const api = treeRef.current;
    const isOpen = (id) => (api ? api.isOpen(id) : true);
    const countVisible = (parentId) =>
      folders
        .filter((f) => f.parent_id === parentId)
        .reduce((acc, f) => {
          const hasChildren = folders.some((c) => c.parent_id === f.id);
          return acc + 1 + (hasChildren && isOpen(f.id) ? countVisible(f.id) : 0);
        }, 0);
    setTreeHeight(Math.max(ROW_HEIGHT, countVisible(null) * ROW_HEIGHT));
  }, [folders]);

  useEffect(() => { syncHeight(); }, [folders, syncHeight]);

  // Drop a dragged task onto a folder → reassign it via the bulk endpoint,
  // then tell any mounted task list to refetch.
  const handleMoveTaskToFolder = useCallback(async (taskIds, folderId) => {
    const ids = (Array.isArray(taskIds) ? taskIds : [taskIds]).filter(Boolean);
    if (!ids.length) return;
    await API.patch('/tasks/bulk', { ids, updates: { folder_id: folderId } });
    window.dispatchEvent(new CustomEvent('focusly:tasks-changed'));
  }, []);

  const handleCreate = (parentId = null) => { setParentIdForNew(parentId); setShowModal(true); };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this folder and all its contents?')) return;
    await API.delete(`/folders/${id}`);
    refreshFolders();
    if (selectedFolderId === id) onSelectFolder(null);
  };

  const handleSaveNew = async (name) => {
    await API.post('/folders', { name, parent_id: parentIdForNew });
    setShowModal(false);
    refreshFolders();
  };

  const handleRename = async ({ id, name }) => {
    if (!name || !name.trim()) return;
    await API.put(`/folders/${id}`, { name: name.trim() });
    refreshFolders();
  };

  // Drag-and-drop: reorder within a parent and/or re-parent
  const handleMove = async ({ dragIds, parentId, index }) => {
    const newParentId = parentId || null;
    const dragSet = new Set(dragIds);

    const siblings = folders.filter(
      (f) => (f.parent_id || null) === newParentId && !dragSet.has(f.id)
    );
    const dragged = dragIds.map((id) => folders.find((f) => f.id === id)).filter(Boolean);
    const ordered = [...siblings];
    ordered.splice(index, 0, ...dragged);
    const orderedIds = ordered.map((f) => f.id);

    // Re-parent any dragged folder whose parent actually changed
    const reparent = dragged
      .filter((f) => (f.parent_id || null) !== newParentId)
      .map((f) => API.put(`/folders/${f.id}`, { parent_id: newParentId }));
    if (reparent.length) await Promise.all(reparent);

    await API.put('/folders/reorder', { parent_id: newParentId, folder_ids: orderedIds });
    refreshFolders();
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center p-2 border-bottom">
        <strong>Folders</strong>
        <button className="btn btn-sm btn-primary" onClick={() => handleCreate(null)}>+ New</button>
      </div>
      <div className="p-2">
        {treeData.length === 0 ? (
          <small className="text-muted">No folders yet</small>
        ) : (
          <Tree
            ref={treeRef}
            data={treeData}
            openByDefault
            width="100%"
            height={treeHeight}
            indent={INDENT}
            rowHeight={ROW_HEIGHT}
            onMove={handleMove}
            onRename={handleRename}
            onToggle={syncHeight}
            disableMultiSelection
            selectedFolderId={selectedFolderId}
            folderMeta={{
              onSelect: onSelectFolder,
              onCreate: handleCreate,
              onDelete: handleDelete,
              onMoveTaskToFolder: handleMoveTaskToFolder,
            }}
          >
            {Node}
          </Tree>
        )}
      </div>
      {showModal && (
        <FolderModal
          initial=""
          title="New Folder"
          onSave={handleSaveNew}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
